import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#syncColorFromImage, { capture: true, signal });
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
 #handleProductSelect = (event) => {
  if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

  const preserveGallery = event.target.dataset.srGallerySelection === 'true';

  event.promise
    .then(({ variant, detail }) => {
      // Unavailable size: keep existing product images
      if (!detail?.html || (!variant && !detail.resource)) return;

      if (preserveGallery) return;

      const newMediaGallery = detail.html.querySelector('media-gallery');
      if (!newMediaGallery) return;

      this.replaceWith(newMediaGallery);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[media-gallery] Event promise rejected:', error);
      }
    });
};

  #colorMap;

  // Shopify width parameters do not change the identity of a product image.
  #imageKey(src) {
    try {
      return new URL(src, window.location.href).pathname
        .replace(/_\d+x\d*(?=\.[^.]+$)/, '');
    } catch {
      return '';
    }
  }

  #syncColorFromImage = (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;
    // Ignore close/arrow controls and media belonging to another gallery.
    if (target.closest('media-gallery') !== this) return;
    const container = target.closest('button, slideshow-slide, .product-media-container');
    const img = target.closest('img') || container?.querySelector('img');
    if (!img) return;

    if (!this.#colorMap) {
      try {
        this.#colorMap = JSON.parse(this.querySelector('[data-sr-media-colors]')?.textContent || 'null');
      } catch { return; }
    }
    const map = this.#colorMap;
    if (!map?.colors?.length) return;
    const key = this.#imageKey(img.currentSrc || img.getAttribute('src'));
    if (!key) return;
    const media = map.media.find((item) => this.#imageKey(item.src) === key);
    if (!media) return;
    const normalize = (value) => String(value).trim().toLowerCase();
    const tag = String(media.alt || '').match(/\[colou?r:\s*([^\]]+)\]/i);
    const matches = map.colors.filter((color) => tag
      ? normalize(color.value) === normalize(tag[1])
      : color.mediaIds.includes(String(media.id)) || normalize(media.alt) === normalize(color.value));
    // Never guess a color from image order, filenames or a shared image.
    if (matches.length !== 1) return;

    const scope = this.closest('.shopify-section, dialog');
    const picker = Array.from(scope?.querySelectorAll('variant-picker') || [])
      .find((element) => element.dataset.productId === map.productId && !element.closest('product-card'));
    if (!picker) return;
    const option = Array.from(picker.querySelectorAll('[data-option-value-id]'))
      .find((element) => element.dataset.optionValueId === matches[0].optionValueId);
    if (!option) return;
    if (option instanceof HTMLInputElement && option.checked) return;
    const select = option.closest('select');
    if (select && option.selected) return;

    // Use the native variant pipeline for price, availability, cart ID and size.
    // Keep this gallery intact while its variant request updates the details.
    picker.dataset.srGallerySelection = 'true';
    try {
      if (select) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (option instanceof HTMLInputElement) {
        option.checked = true;
        option.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } finally {
      delete picker.dataset.srGallerySelection;
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
